/* custom code start */
/**
 * <occ-cart-recommendations>
 *
 * Sepet yan panelindeki "Bunu da beğenebilirsiniz" bloğu.
 * Öneriler Shopify Product Recommendations endpoint'inden (Search & Discovery
 * "related products" verisi) occ-cart-recommendations section'ı render edilerek
 * alınır. Sepette zaten bulunan ürünler istemci tarafında elenir.
 */
if (!customElements.get('occ-cart-recommendations')) {
  /**
   * Sepet her güncellendiğinde drawer baştan render edildiği için aynı ürünün
   * önerileri tekrar tekrar istenmesin diye yanıtlar sayfa ömrü boyunca
   * bellekte tutulur. Sepete göre eleme zaten istemci tarafında yapılır.
   */
  const recommendationsCache = new Map();

  class OccCartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.cartProductIds = new Set(
        (this.dataset.cartProductIds || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean)
      );
    }

    connectedCallback() {
      this.loadRecommendations();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
    }

    get cartDrawer() {
      return this.closest('cart-drawer') || document.querySelector('cart-drawer');
    }

    buildUrl() {
      const base = this.dataset.url || `${window.Shopify.routes.root}recommendations/products`;
      const params = new URLSearchParams({
        section_id: this.dataset.sectionId || 'occ-cart-recommendations',
        product_id: this.dataset.productId,
        limit: this.dataset.limit || '10',
        intent: this.dataset.intent || 'related',
      });

      return `${base}?${params.toString()}`;
    }

    loadRecommendations() {
      if (!this.dataset.productId) return;

      const url = this.buildUrl();
      const cached = recommendationsCache.get(url);

      if (cached !== undefined) {
        this.render(cached);
        return;
      }

      this.abortController = new AbortController();

      fetch(url, { signal: this.abortController.signal })
        .then((response) => {
          if (!response.ok) throw new Error(response.statusText);
          return response.text();
        })
        .then((text) => {
          const parsed = new DOMParser().parseFromString(text, 'text/html');
          const block = parsed.querySelector('[data-occ-cart-recs-block]');
          if (!block) return;

          recommendationsCache.set(url, block.innerHTML);
          this.render(block.innerHTML);
        })
        .catch((error) => {
          if (error.name === 'AbortError') return;
          console.error('occ-cart-recommendations:', error);
          this.innerHTML = '';
        });
    }

    render(html) {
      this.innerHTML = html;
      this.removeCartProducts();

      if (!this.querySelector('.occ-cart-recs__item')) {
        this.innerHTML = '';
        return;
      }

      this.classList.add('occ-cart-recs--loaded');
      this.bindAddButtons();
      this.watchScrollState();
    }

    /**
     * Kaydırma çubuğu CSS'te gizlendiği için listenin devamı olup olmadığı
     * alt kenardaki yumuşak geçişle belirtilir. Drawer kapalıyken yükseklik
     * 0 okunacağından durum ResizeObserver ile de takip edilir.
     */
    watchScrollState() {
      const list = this.querySelector('.occ-cart-recs__list');
      if (!list) return;

      const update = () => {
        const overflows = list.scrollHeight - list.clientHeight > 1;
        const atEnd = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;

        this.classList.toggle('occ-cart-recs--no-overflow', !overflows);
        this.classList.toggle('occ-cart-recs--scroll-end', overflows && atEnd);
      };

      list.addEventListener('scroll', update, { passive: true });

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver?.disconnect();
        this.resizeObserver = new ResizeObserver(update);
        this.resizeObserver.observe(list);
      }

      update();
    }

    /**
     * Sepete eklenen ürünler öneri listesinden çıkar. Endpoint yanıtı sepete
     * göre değişmediğinden bu filtre istemci tarafında uygulanır.
     */
    removeCartProducts() {
      if (this.cartProductIds.size === 0) return;

      this.querySelectorAll('.occ-cart-recs__item').forEach((item) => {
        if (this.cartProductIds.has(item.dataset.productId)) item.remove();
      });
    }

    bindAddButtons() {
      this.querySelectorAll('.occ-cart-recs__add[data-variant-id]').forEach((button) => {
        button.addEventListener('click', this.handleAdd.bind(this));
      });
    }

    handleAdd(event) {
      const button = event.currentTarget;
      if (button.getAttribute('aria-disabled') === 'true') return;

      const variantId = button.dataset.variantId;
      const item = button.closest('.occ-cart-recs__item');
      if (!variantId) return;

      button.setAttribute('aria-disabled', 'true');
      button.classList.add('occ-cart-recs__add--loading');

      const cartDrawer = this.cartDrawer;
      const body = {
        items: [{ id: Number(variantId), quantity: 1 }],
      };

      if (cartDrawer) {
        body.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
        cartDrawer.setActiveElement(document.activeElement);
      }

      fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(body),
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'occ-cart-recommendations',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
            }
            button.removeAttribute('aria-disabled');
            button.classList.remove('occ-cart-recs__add--loading');
            return;
          }

          // Eklenen ürün listeden hemen kaldırılır; drawer yeniden render
          // edildiğinde liste zaten güncel sepete göre tekrar kurulur.
          item?.remove();

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            const list = this.querySelector('[data-tracking-list-id]');
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'occ-cart-recommendations',
              productVariantId: variantId,
              cartData: response,
              listId: list ? list.getAttribute('data-tracking-list-id') : 'cart_recommendations',
              listName: list ? list.getAttribute('data-tracking-list-name') : '',
            });
          }

          if (cartDrawer && response.sections) {
            cartDrawer.classList.remove('is-empty');
            cartDrawer.renderContents(response);
          } else {
            window.location.reload();
          }
        })
        .catch((error) => {
          console.error('occ-cart-recommendations:', error);
          button.removeAttribute('aria-disabled');
          button.classList.remove('occ-cart-recs__add--loading');
        });
    }
  }

  customElements.define('occ-cart-recommendations', OccCartRecommendations);
}
/* custom code end */
